import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  Submittal, RFI, SubmittalStatus, RFIStatus, RFIPriority, Project,
} from '../../../types';
import {
  getProjectSubmittals, createSubmittal, updateSubmittal, deleteSubmittal,
  getProjectRFIs, createRFI, updateRFI, deleteRFI,
  suggestNextNumber,
} from '../../../services/constructionService';
import PeoplePicker, { Person } from '../../../components/PeoplePicker';
import ModuleGuard from '../../../components/ModuleGuard';
import Badge from '../../../components/Badge';
import AutoSizingTextarea from '../../../components/AutoSizingTextarea';
import BoardModal, {
  BMCancelButton, BMPrimaryButton, BMDangerButton,
  BM_LABEL, BM_INPUT, BM_TITLE_INPUT,
} from '../../../components/BoardModal';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  ApprovalStage, ApprovalEntityType, ApprovalStageStatus,
  findProjectChainForEntityType, getEntityStages,
  instantiateStagesForEntity, approveStage, rejectStage,
} from '../../../services/approvalService';
import ApprovalStagesSection from '../ApprovalStagesSection';

interface Props { pca: IPublicClientApplication; projectId: string; project: Project; }

// ── Shared styling helpers ────────────────────────────────────────

const SUBMITTAL_STATUS_LABEL: Record<number, string> = {
  [SubmittalStatus.Draft]:           'Draft',
  [SubmittalStatus.Submitted]:       'Submitted',
  [SubmittalStatus.InReview]:        'In Review',
  [SubmittalStatus.Approved]:        'Approved',
  [SubmittalStatus.ApprovedAsNoted]: 'Approved as Noted',
  [SubmittalStatus.Rejected]:        'Rejected',
  [SubmittalStatus.Resubmit]:        'Resubmit',
};
const SUBMITTAL_STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [SubmittalStatus.Draft]:           { bg: '#F3F4F6', fg: '#5A6272' },
  [SubmittalStatus.Submitted]:       { bg: '#D4EEF5', fg: '#0A5568' },
  [SubmittalStatus.InReview]:        { bg: '#FEF3C7', fg: '#92400E' },
  [SubmittalStatus.Approved]:        { bg: '#D1FAE5', fg: '#065F46' },
  [SubmittalStatus.ApprovedAsNoted]: { bg: '#CCFBF1', fg: '#115E59' },
  [SubmittalStatus.Rejected]:        { bg: '#FEE2E2', fg: '#991B1B' },
  [SubmittalStatus.Resubmit]:        { bg: '#FED7AA', fg: '#9A3412' },
};
const SUBMITTAL_STATUS_ORDER = [
  SubmittalStatus.Draft, SubmittalStatus.Submitted, SubmittalStatus.InReview,
  SubmittalStatus.Approved, SubmittalStatus.ApprovedAsNoted, SubmittalStatus.Rejected,
  SubmittalStatus.Resubmit,
];

const RFI_STATUS_LABEL: Record<number, string> = {
  [RFIStatus.Open]: 'Open', [RFIStatus.InReview]: 'In Review',
  [RFIStatus.Answered]: 'Answered', [RFIStatus.Closed]: 'Closed',
};
const RFI_STATUS_BADGE: Record<number, { bg: string; fg: string }> = {
  [RFIStatus.Open]:     { bg: '#D4EEF5', fg: '#0A5568' },
  [RFIStatus.InReview]: { bg: '#FEF3C7', fg: '#92400E' },
  [RFIStatus.Answered]: { bg: '#D1FAE5', fg: '#065F46' },
  [RFIStatus.Closed]:   { bg: '#F3F4F6', fg: '#5A6272' },
};
const RFI_PRIORITY_LABEL: Record<number, string> = {
  [RFIPriority.Low]: 'Low', [RFIPriority.Medium]: 'Medium',
  [RFIPriority.High]: 'High', [RFIPriority.Critical]: 'Critical',
};
const RFI_PRIORITY_BADGE: Record<number, { bg: string; fg: string }> = {
  [RFIPriority.Low]:      { bg: '#F3F4F6', fg: '#5A6272' },
  [RFIPriority.Medium]:   { bg: '#D4EEF5', fg: '#0A5568' },
  [RFIPriority.High]:     { bg: '#FEF3C7', fg: '#92400E' },
  [RFIPriority.Critical]: { bg: '#FEE2E2', fg: '#991B1B' },
};

// ── Entry point ───────────────────────────────────────────────────

export default function ConstructionTab(props: Props) {
  return (
    <ModuleGuard project={props.project} flag="dagrun_pm_module_submittalrfi">
      <ConstructionTabBody {...props} />
    </ModuleGuard>
  );
}

type SubTab = 'submittals' | 'rfis';

function ConstructionTabBody({ pca, projectId }: Props) {
  const [subTab, setSubTab] = useState<SubTab>('submittals');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 24px',
        borderBottom: '0.5px solid #EAECF0',
      }}>
        <div style={{
          display: 'inline-flex', gap: 2, padding: 2,
          background: '#F5F6F8', borderRadius: 5,
        }}>
          <SubTabButton active={subTab === 'submittals'} label="Submittal Log" onClick={() => setSubTab('submittals')} />
          <SubTabButton active={subTab === 'rfis'}       label="RFI Log"        onClick={() => setSubTab('rfis')} />
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {subTab === 'submittals'
          ? <SubmittalLog pca={pca} projectId={projectId} />
          : <RFILog       pca={pca} projectId={projectId} />}
      </div>
    </div>
  );
}

function SubTabButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        border: 'none',
        background: active ? '#D4EEF5' : 'transparent',
        color: active ? '#0E7490' : '#5A6272',
        fontWeight: active ? 500 : 400,
        fontFamily: 'var(--font-ui)',
        fontSize: 12, padding: '4px 10px',
        borderRadius: 4, cursor: 'pointer',
      }}
    >{label}</button>
  );
}

// ───────────────────────────────────────────────────────────────────
// Submittal Log
// ───────────────────────────────────────────────────────────────────

function SubmittalLog({ pca, projectId }: { pca: IPublicClientApplication; projectId: string }) {
  const [items, setItems] = useState<Submittal[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<Submittal | null>(null);

  const reload = useCallback(() => getProjectSubmittals(pca, projectId).then(setItems), [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>Submittal Log</h3>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Add Submittal</button>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No submittals logged yet.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>#</Th><Th>Description</Th><Th>Spec</Th><Th>Submitted By</Th><Th>Submitted</Th><Th>Required</Th><Th>Status</Th><Th>Reviewer</Th>
          </tr></thead>
          <tbody>
            {items.map(s => (
              <tr key={s.dagrun_pm_submittalid} onClick={() => setSelected(s)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#5A6272' }}>{s.dagrun_pm_submittalnumber ?? <Em />}</span></Td>
                <Td><span title={s.dagrun_pm_name} style={{
                  color: '#0F1117', fontSize: 14,
                  display: 'inline-block', maxWidth: 280,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  verticalAlign: 'middle',
                }}>{s.dagrun_pm_name}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272' }}>{s.dagrun_pm_spec ?? <Em />}</span></Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{s.dagrun_pm_submittedbyname ?? <Em />}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {s.dagrun_pm_submitteddate ? new Date(s.dagrun_pm_submitteddate).toLocaleDateString() : '—'}
                </span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {s.dagrun_pm_requireddate ? new Date(s.dagrun_pm_requireddate).toLocaleDateString() : '—'}
                </span></Td>
                <Td>{s.dagrun_pm_status != null
                  ? <Badge {...SUBMITTAL_STATUS_BADGE[s.dagrun_pm_status]}>{SUBMITTAL_STATUS_LABEL[s.dagrun_pm_status]}</Badge>
                  : <Em />}</Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{s.dagrun_pm_reviewername ?? <Em />}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateSubmittalModal pca={pca} projectId={projectId} existingNumbers={items.map(i => i.dagrun_pm_submittalnumber)}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <SubmittalDrawer pca={pca} submittal={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

function CreateSubmittalModal({ pca, projectId, existingNumbers, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string;
  existingNumbers: (string | null | undefined)[];
  onClose: () => void; onCreated: () => void;
}) {
  const me = useCurrentUser(pca, projectId);
  const suggested = suggestNextNumber('S-', existingNumbers);
  const [number, setNumber] = useState(suggested);
  const [description, setDescription] = useState('');
  const [spec, setSpec] = useState('');
  const [submittedBy, setSubmittedBy] = useState<Person | null>(null);
  const [submittedDate, setSubmittedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [requiredDate, setRequiredDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me && !submittedBy) setSubmittedBy({ aadObjectId: me.oid, displayName: me.displayName });
  }, [me, submittedBy]);

  const submit = async () => {
    if (!description.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createSubmittal(pca, {
        projectId,
        name: description.trim(),
        submittalnumber: number.trim() || null,
        spec: spec.trim() || null,
        submittedbyid: submittedBy?.aadObjectId ?? null,
        submittedbyname: submittedBy?.displayName ?? null,
        submitteddate: submittedDate ? new Date(submittedDate).toISOString() : null,
        requireddate: requiredDate ? new Date(requiredDate).toISOString() : null,
        notes: notes.trim() || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add Submittal" onClose={onClose} width={560}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !description.trim()}>
          {saving ? 'Creating…' : 'Add Submittal'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Description *</label>
        <input autoFocus value={description} onChange={e => setDescription(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="e.g. Steel shop drawings Level 1" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Submittal #</label>
          <input value={number} onChange={e => setNumber(e.target.value)}
            style={BM_INPUT} placeholder={suggested} />
        </div>
        <div>
          <label style={BM_LABEL}>Spec Section</label>
          <input value={spec} onChange={e => setSpec(e.target.value)} style={BM_INPUT} placeholder="e.g. 05 12 00" />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Submitted By</label>
        <PeoplePicker pca={pca} value={submittedBy} onChange={setSubmittedBy} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Submitted Date</label>
          <input type="date" value={submittedDate} onChange={e => setSubmittedDate(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Required Date</label>
          <input type="date" value={requiredDate} onChange={e => setRequiredDate(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Notes</label>
        <AutoSizingTextarea value={notes} onChange={setNotes} />
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function SubmittalDrawer({ pca, submittal, onClose, onDeleted }: {
  pca: IPublicClientApplication; submittal: Submittal;
  onClose: () => void; onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<Submittal>(submittal);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);
  const projectId = submittal.dagrun_pm_projectid ?? '';
  const me = useCurrentUser(pca, projectId);
  const [approvalStages, setApprovalStages] = useState<ApprovalStage[] | null>(null);
  const [stagesLoading, setStagesLoading] = useState(true);

  useEffect(() => { setDraft(submittal); }, [submittal]);

  // Load approval stages attached to THIS submittal.
  const reloadStages = useCallback(async () => {
    setStagesLoading(true);
    try { setApprovalStages(await getEntityStages(pca, submittal.dagrun_pm_submittalid)); }
    finally { setStagesLoading(false); }
  }, [pca, submittal.dagrun_pm_submittalid]);
  useEffect(() => { reloadStages(); }, [reloadStages]);

  const patch = async (fields: Partial<Submittal>) => {
    setSaving(true);
    try {
      await updateSubmittal(pca, draft.dagrun_pm_submittalid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };
  const setAndPatch = <K extends keyof Submittal>(k: K, v: Submittal[K]) => {
    setDraft(d => ({ ...d, [k]: v })); patch({ [k]: v } as Partial<Submittal>);
  };
  const setText = <K extends keyof Submittal>(k: K, v: Submittal[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<Submittal>), 600);
  };
  const flush = <K extends keyof Submittal>(k: K, v: Submittal[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<Submittal>);
  };
  const doDelete = async () => {
    setSaving(true);
    try { await deleteSubmittal(pca, draft.dagrun_pm_submittalid); onDeleted(); } finally { setSaving(false); }
  };

  // When status changes to Submitted and no submitteddate is set, default to today.
  // Additionally, if a Submittal approval chain exists for this project and
  // no instance stages exist yet for this submittal, instantiate them.
  const handleStatusChange = async (nextStatus: number) => {
    const fields: Partial<Submittal> = { dagrun_pm_status: nextStatus };
    if (nextStatus === SubmittalStatus.Submitted && !draft.dagrun_pm_submitteddate) {
      fields.dagrun_pm_submitteddate = new Date().toISOString();
    }
    // When moving to Approved/Rejected/Resubmit, stamp reviewed date.
    if ([SubmittalStatus.Approved, SubmittalStatus.ApprovedAsNoted, SubmittalStatus.Rejected, SubmittalStatus.Resubmit].includes(nextStatus as 4 | 5 | 6 | 7)
        && !draft.dagrun_pm_revieweddate) {
      fields.dagrun_pm_revieweddate = new Date().toISOString();
    }
    setDraft(d => ({ ...d, ...fields }));
    await patch(fields);

    if (nextStatus === SubmittalStatus.Submitted && projectId) {
      const existing = approvalStages ?? [];
      if (existing.length === 0) {
        try {
          const chain = await findProjectChainForEntityType(pca, projectId, ApprovalEntityType.Submittal);
          if (chain) {
            const label = draft.dagrun_pm_submittalnumber ?? draft.dagrun_pm_name;
            await instantiateStagesForEntity(pca, chain.dagrun_pm_approvalchainid, draft.dagrun_pm_submittalid, label);
            reloadStages();
          }
        } catch { /* non-fatal — don't block the status change */ }
      }
    }
  };

  const onStageDecision = async (stage: ApprovalStage, decision: 'approve' | 'reject', comments?: string) => {
    try {
      if (decision === 'approve') {
        await approveStage(pca, stage.dagrun_pm_approvalstageid, comments ?? null);
      } else {
        await rejectStage(pca, stage.dagrun_pm_approvalstageid, comments ?? '(rejected without comment)');
      }
      const fresh = await getEntityStages(pca, draft.dagrun_pm_submittalid);
      setApprovalStages(fresh);

      // Auto-advance submittal status: all stages approved → Submittal Approved.
      // Any stage rejected → Submittal Rejected.
      if (fresh.length > 0) {
        const anyRejected = fresh.some(s => s.dagrun_pm_status === ApprovalStageStatus.Rejected);
        const allApproved = fresh.every(s => s.dagrun_pm_status === ApprovalStageStatus.Approved);
        if (anyRejected && draft.dagrun_pm_status !== SubmittalStatus.Rejected) {
          setDraft(d => ({ ...d, dagrun_pm_status: SubmittalStatus.Rejected, dagrun_pm_revieweddate: new Date().toISOString() }));
          await patch({ dagrun_pm_status: SubmittalStatus.Rejected, dagrun_pm_revieweddate: new Date().toISOString() });
        } else if (allApproved && draft.dagrun_pm_status !== SubmittalStatus.Approved) {
          setDraft(d => ({ ...d, dagrun_pm_status: SubmittalStatus.Approved, dagrun_pm_revieweddate: new Date().toISOString() }));
          await patch({ dagrun_pm_status: SubmittalStatus.Approved, dagrun_pm_revieweddate: new Date().toISOString() });
        }
      }
    } catch { /* best effort */ }
  };

  const showReviewedDate = draft.dagrun_pm_status === SubmittalStatus.Approved
    || draft.dagrun_pm_status === SubmittalStatus.ApprovedAsNoted
    || draft.dagrun_pm_status === SubmittalStatus.Rejected
    || draft.dagrun_pm_status === SubmittalStatus.Resubmit;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 540,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <div style={{ fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
          {draft.dagrun_pm_submittalnumber ?? 'No number'}
        </div>
        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 12, width: '100%', background: 'transparent' }} />

        <div style={{ marginBottom: 16 }}>
          <select value={draft.dagrun_pm_status ?? SubmittalStatus.Draft}
            onChange={e => handleStatusChange(Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {SUBMITTAL_STATUS_ORDER.map(v =>
              <option key={v} value={v}>{SUBMITTAL_STATUS_LABEL[v]}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Submittal #</label>
            <input value={draft.dagrun_pm_submittalnumber ?? ''}
              onChange={e => setText('dagrun_pm_submittalnumber', e.target.value || null)}
              onBlur={e => flush('dagrun_pm_submittalnumber', e.target.value || null)} style={BM_INPUT} />
          </div>
          <div>
            <label style={BM_LABEL}>Spec Section</label>
            <input value={draft.dagrun_pm_spec ?? ''}
              onChange={e => setText('dagrun_pm_spec', e.target.value || null)}
              onBlur={e => flush('dagrun_pm_spec', e.target.value || null)} style={BM_INPUT} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Submitted Date</label>
            <input type="date" value={draft.dagrun_pm_submitteddate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_submitteddate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
          </div>
          <div>
            <label style={BM_LABEL}>Required Date</label>
            <input type="date" value={draft.dagrun_pm_requireddate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_requireddate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Submitted By</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_submittedbyid ? { aadObjectId: draft.dagrun_pm_submittedbyid, displayName: draft.dagrun_pm_submittedbyname ?? draft.dagrun_pm_submittedbyid } : null}
            onChange={p => {
              const fields = {
                dagrun_pm_submittedbyid: p?.aadObjectId ?? null,
                dagrun_pm_submittedbyname: p?.displayName ?? null,
              };
              setDraft(d => ({ ...d, ...fields })); patch(fields);
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Reviewer</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_reviewerid ? { aadObjectId: draft.dagrun_pm_reviewerid, displayName: draft.dagrun_pm_reviewername ?? draft.dagrun_pm_reviewerid } : null}
            onChange={p => {
              const fields = {
                dagrun_pm_reviewerid: p?.aadObjectId ?? null,
                dagrun_pm_reviewername: p?.displayName ?? null,
              };
              setDraft(d => ({ ...d, ...fields })); patch(fields);
            }}
          />
        </div>
        {showReviewedDate && (
          <div style={{ marginBottom: 12 }}>
            <label style={BM_LABEL}>Reviewed Date</label>
            <input type="date" value={draft.dagrun_pm_revieweddate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_revieweddate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Notes</label>
          <AutoSizingTextarea
            value={draft.dagrun_pm_notes ?? ''}
            onChange={v => setText('dagrun_pm_notes', v || null)}
            onBlur={v => flush('dagrun_pm_notes', v || null)}
          />
        </div>

        <ApprovalStagesSection
          pca={pca}
          stages={approvalStages}
          loading={stagesLoading}
          myOid={me?.oid}
          onDecision={onStageDecision}
          entityLabel="submittal"
          triggerStatusLabel="Submitted"
        />

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete Submittal" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete submittal <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_submittalnumber ?? draft.dagrun_pm_name}</strong>?
        </BoardModal>
      )}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────
// RFI Log
// ───────────────────────────────────────────────────────────────────

function RFILog({ pca, projectId }: { pca: IPublicClientApplication; projectId: string }) {
  const [items, setItems] = useState<RFI[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<RFI | null>(null);

  const reload = useCallback(() => getProjectRFIs(pca, projectId).then(setItems), [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={{ padding: '20px 32px', fontFamily: 'var(--font-ui)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0F1117' }}>RFI Log</h3>
        <button type="button" onClick={() => setShowNew(true)}
          style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#0A5568')}
          onMouseLeave={e => (e.currentTarget.style.background = '#0E7490')}
        >+ Add RFI</button>
      </div>

      {items.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#9198A8', fontSize: 14 }}>
          No RFIs logged yet.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '1px solid #DDE0E6', textAlign: 'left' }}>
            <Th>#</Th><Th>Question</Th><Th>Priority</Th><Th>Submitted By</Th><Th>Submitted</Th><Th>Due</Th><Th>Status</Th><Th>Assigned To</Th>
          </tr></thead>
          <tbody>
            {items.map(r => (
              <tr key={r.dagrun_pm_rfiid} onClick={() => setSelected(r)}
                style={{ borderBottom: '0.5px solid #EAECF0', cursor: 'pointer', transition: 'background 0.12s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#F5F6F8')}
                onMouseLeave={e => (e.currentTarget.style.background = '')}>
                <Td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: '#5A6272' }}>{r.dagrun_pm_rfinumber ?? <Em />}</span></Td>
                <Td><span title={r.dagrun_pm_name} style={{
                  color: '#0F1117', fontSize: 14,
                  display: 'inline-block', maxWidth: 300,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  verticalAlign: 'middle',
                }}>{r.dagrun_pm_name}</span></Td>
                <Td>{r.dagrun_pm_priority != null
                  ? <Badge {...RFI_PRIORITY_BADGE[r.dagrun_pm_priority]}>{RFI_PRIORITY_LABEL[r.dagrun_pm_priority]}</Badge>
                  : <Em />}</Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{r.dagrun_pm_submittedbyname ?? <Em />}</span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {r.dagrun_pm_submitteddate ? new Date(r.dagrun_pm_submitteddate).toLocaleDateString() : '—'}
                </span></Td>
                <Td><span style={{ fontSize: 12, color: '#5A6272', fontFamily: 'var(--font-mono)' }}>
                  {r.dagrun_pm_duedate ? new Date(r.dagrun_pm_duedate).toLocaleDateString() : '—'}
                </span></Td>
                <Td>{r.dagrun_pm_status != null
                  ? <Badge {...RFI_STATUS_BADGE[r.dagrun_pm_status]}>{RFI_STATUS_LABEL[r.dagrun_pm_status]}</Badge>
                  : <Em />}</Td>
                <Td><span style={{ fontSize: 13, color: '#5A6272' }}>{r.dagrun_pm_assignedtoname ?? <Em />}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <CreateRFIModal pca={pca} projectId={projectId} existingNumbers={items.map(i => i.dagrun_pm_rfinumber)}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); reload(); }}
        />
      )}
      {selected && (
        <RFIDrawer pca={pca} rfi={selected}
          onClose={() => { setSelected(null); reload(); }}
          onDeleted={() => { setSelected(null); reload(); }}
        />
      )}
    </div>
  );
}

function CreateRFIModal({ pca, projectId, existingNumbers, onClose, onCreated }: {
  pca: IPublicClientApplication; projectId: string;
  existingNumbers: (string | null | undefined)[];
  onClose: () => void; onCreated: () => void;
}) {
  const me = useCurrentUser(pca, projectId);
  const suggested = suggestNextNumber('RFI-', existingNumbers);
  const [number, setNumber] = useState(suggested);
  const [title, setTitle] = useState('');
  const [question, setQuestion] = useState('');
  const [priority, setPriority] = useState<number>(RFIPriority.Medium);
  const [submittedBy, setSubmittedBy] = useState<Person | null>(null);
  const [submittedDate, setSubmittedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [duedate, setDuedate] = useState('');
  const [assignee, setAssignee] = useState<Person | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me && !submittedBy) setSubmittedBy({ aadObjectId: me.oid, displayName: me.displayName });
  }, [me, submittedBy]);

  const submit = async () => {
    if (!title.trim() || saving) return;
    setSaving(true); setError(null);
    try {
      await createRFI(pca, {
        projectId,
        name: title.trim(),
        rfinumber: number.trim() || null,
        question: question.trim() || null,
        priority,
        submittedbyid: submittedBy?.aadObjectId ?? null,
        submittedbyname: submittedBy?.displayName ?? null,
        submitteddate: submittedDate ? new Date(submittedDate).toISOString() : null,
        duedate: duedate ? new Date(duedate).toISOString() : null,
        assignedtoid: assignee?.aadObjectId ?? null,
        assignedtoname: assignee?.displayName ?? null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <BoardModal open title="Add RFI" onClose={onClose} width={560}
      footer={<>
        <BMCancelButton onClick={onClose} disabled={saving} />
        <BMPrimaryButton onClick={submit} disabled={saving || !title.trim()}>
          {saving ? 'Creating…' : 'Add RFI'}
        </BMPrimaryButton>
      </>}>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Short Question *</label>
        <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
          style={BM_TITLE_INPUT} placeholder="One-line summary of the question" />
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Full Question</label>
        <AutoSizingTextarea value={question} onChange={setQuestion} placeholder="Full question body with details, context, what response is needed…" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>RFI #</label>
          <input value={number} onChange={e => setNumber(e.target.value)} style={BM_INPUT} placeholder={suggested} />
        </div>
        <div>
          <label style={BM_LABEL}>Priority</label>
          <select value={priority} onChange={e => setPriority(Number(e.target.value))} style={BM_INPUT}>
            {[RFIPriority.Low, RFIPriority.Medium, RFIPriority.High, RFIPriority.Critical].map(v =>
              <option key={v} value={v}>{RFI_PRIORITY_LABEL[v]}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Submitted By</label>
        <PeoplePicker pca={pca} value={submittedBy} onChange={setSubmittedBy} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={BM_LABEL}>Submitted Date</label>
          <input type="date" value={submittedDate} onChange={e => setSubmittedDate(e.target.value)} style={BM_INPUT} />
        </div>
        <div>
          <label style={BM_LABEL}>Due Date</label>
          <input type="date" value={duedate} onChange={e => setDuedate(e.target.value)} style={BM_INPUT} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <label style={BM_LABEL}>Assigned To</label>
        <PeoplePicker pca={pca} value={assignee} onChange={setAssignee} placeholder="Assign to…" />
      </div>
      {error && <div style={{ marginTop: 12, color: '#DC2626', fontSize: 12 }}>{error}</div>}
    </BoardModal>
  );
}

function RFIDrawer({ pca, rfi, onClose, onDeleted }: {
  pca: IPublicClientApplication; rfi: RFI;
  onClose: () => void; onDeleted: () => void;
}) {
  const [draft, setDraft] = useState<RFI>(rfi);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const textDebounce = useRef<number | null>(null);
  const projectId = rfi.dagrun_pm_projectid ?? '';
  const me = useCurrentUser(pca, projectId);
  const [approvalStages, setApprovalStages] = useState<ApprovalStage[] | null>(null);
  const [stagesLoading, setStagesLoading] = useState(true);

  useEffect(() => { setDraft(rfi); }, [rfi]);

  const reloadStages = useCallback(async () => {
    setStagesLoading(true);
    try { setApprovalStages(await getEntityStages(pca, rfi.dagrun_pm_rfiid)); }
    finally { setStagesLoading(false); }
  }, [pca, rfi.dagrun_pm_rfiid]);
  useEffect(() => { reloadStages(); }, [reloadStages]);

  const patch = async (fields: Partial<RFI>) => {
    setSaving(true);
    try {
      await updateRFI(pca, draft.dagrun_pm_rfiid, fields as Record<string, unknown>);
      setSavedFlash(true);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } finally { setSaving(false); }
  };
  const setAndPatch = <K extends keyof RFI>(k: K, v: RFI[K]) => {
    setDraft(d => ({ ...d, [k]: v })); patch({ [k]: v } as Partial<RFI>);
  };
  const setText = <K extends keyof RFI>(k: K, v: RFI[K]) => {
    setDraft(d => ({ ...d, [k]: v }));
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    textDebounce.current = window.setTimeout(() => patch({ [k]: v } as Partial<RFI>), 600);
  };
  const flush = <K extends keyof RFI>(k: K, v: RFI[K]) => {
    if (textDebounce.current) window.clearTimeout(textDebounce.current);
    patch({ [k]: v } as Partial<RFI>);
  };
  const doDelete = async () => {
    setSaving(true);
    try { await deleteRFI(pca, draft.dagrun_pm_rfiid); onDeleted(); } finally { setSaving(false); }
  };

  const handleStatusChange = async (next: number) => {
    const fields: Partial<RFI> = { dagrun_pm_status: next };
    if (next === RFIStatus.Answered && !draft.dagrun_pm_answereddate) {
      fields.dagrun_pm_answereddate = new Date().toISOString();
    }
    setDraft(d => ({ ...d, ...fields }));
    await patch(fields);

    // On transition to In Review, if the project has an RFI approval chain
    // configured AND no instance stages exist yet for this RFI, instantiate
    // them — same pattern as the Submittal approval flow.
    if (next === RFIStatus.InReview && projectId) {
      const existing = approvalStages ?? [];
      if (existing.length === 0) {
        try {
          const chain = await findProjectChainForEntityType(pca, projectId, ApprovalEntityType.RFI);
          if (chain) {
            const label = draft.dagrun_pm_rfinumber ?? draft.dagrun_pm_name;
            await instantiateStagesForEntity(pca, chain.dagrun_pm_approvalchainid, draft.dagrun_pm_rfiid, label);
            reloadStages();
          }
        } catch { /* non-fatal */ }
      }
    }
  };

  const onStageDecision = async (stage: ApprovalStage, decision: 'approve' | 'reject', comments?: string) => {
    try {
      if (decision === 'approve') {
        await approveStage(pca, stage.dagrun_pm_approvalstageid, comments ?? null);
      } else {
        await rejectStage(pca, stage.dagrun_pm_approvalstageid, comments ?? '(rejected without comment)');
      }
      const fresh = await getEntityStages(pca, draft.dagrun_pm_rfiid);
      setApprovalStages(fresh);

      // Auto-advance RFI status: all approved → Answered + stamp answered
      // date; any rejected → stays Open with rejection visible on the
      // stage (matches spec guidance — rejections don't auto-close RFIs).
      if (fresh.length > 0) {
        const allApproved = fresh.every(s => s.dagrun_pm_status === ApprovalStageStatus.Approved);
        if (allApproved && draft.dagrun_pm_status !== RFIStatus.Answered) {
          const fields: Partial<RFI> = {
            dagrun_pm_status: RFIStatus.Answered,
            dagrun_pm_answereddate: new Date().toISOString(),
          };
          setDraft(d => ({ ...d, ...fields }));
          await patch(fields);
        }
      }
    } catch { /* best effort */ }
  };

  const showAnswer = draft.dagrun_pm_status === RFIStatus.Answered
    || draft.dagrun_pm_status === RFIStatus.Closed;

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.18)', zIndex: 100 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, height: '100%', width: 560,
        background: '#fff', borderLeft: '0.5px solid #EAECF0',
        boxShadow: '0 4px 24px rgba(15,17,23,0.12)', zIndex: 101,
        overflowY: 'auto', padding: '16px 24px 32px 24px',
        fontFamily: 'var(--font-ui)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9198A8', opacity: savedFlash ? 1 : 0, transition: 'opacity 0.2s' }}>
            {savedFlash ? 'Saved ✓' : ''}
          </span>
          <button onClick={onClose} aria-label="Close" style={{ width: 28, height: 28, border: 'none', background: 'transparent', cursor: 'pointer', color: '#5A6272', fontSize: 18 }}>×</button>
        </div>

        <div style={{ fontSize: 11, color: '#9198A8', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>
          {draft.dagrun_pm_rfinumber ?? 'No number'}
        </div>
        <input value={draft.dagrun_pm_name}
          onChange={e => setText('dagrun_pm_name', e.target.value)}
          onBlur={e => flush('dagrun_pm_name', e.target.value)}
          style={{ fontSize: 18, fontWeight: 600, color: '#0F1117', border: 'none', padding: 0, marginBottom: 12, width: '100%', background: 'transparent' }} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          <select value={draft.dagrun_pm_status ?? RFIStatus.Open}
            onChange={e => handleStatusChange(Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[RFIStatus.Open, RFIStatus.InReview, RFIStatus.Answered, RFIStatus.Closed].map(v =>
              <option key={v} value={v}>{RFI_STATUS_LABEL[v]}</option>)}
          </select>
          <select value={draft.dagrun_pm_priority ?? RFIPriority.Medium}
            onChange={e => setAndPatch('dagrun_pm_priority', Number(e.target.value))}
            style={{ ...BM_INPUT, width: 'auto', padding: '4px 8px', fontSize: 12 }}>
            {[RFIPriority.Low, RFIPriority.Medium, RFIPriority.High, RFIPriority.Critical].map(v =>
              <option key={v} value={v}>{RFI_PRIORITY_LABEL[v]}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Full Question</label>
          <AutoSizingTextarea
            value={draft.dagrun_pm_question ?? ''}
            onChange={v => setText('dagrun_pm_question', v || null)}
            onBlur={v => flush('dagrun_pm_question', v || null)}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={BM_LABEL}>Submitted Date</label>
            <input type="date" value={draft.dagrun_pm_submitteddate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_submitteddate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
          </div>
          <div>
            <label style={BM_LABEL}>Due Date</label>
            <input type="date" value={draft.dagrun_pm_duedate?.slice(0, 10) ?? ''}
              onChange={e => setAndPatch('dagrun_pm_duedate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Submitted By</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_submittedbyid ? { aadObjectId: draft.dagrun_pm_submittedbyid, displayName: draft.dagrun_pm_submittedbyname ?? draft.dagrun_pm_submittedbyid } : null}
            onChange={p => {
              const fields = {
                dagrun_pm_submittedbyid: p?.aadObjectId ?? null,
                dagrun_pm_submittedbyname: p?.displayName ?? null,
              };
              setDraft(d => ({ ...d, ...fields })); patch(fields);
            }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={BM_LABEL}>Assigned To</label>
          <PeoplePicker pca={pca}
            value={draft.dagrun_pm_assignedtoid ? { aadObjectId: draft.dagrun_pm_assignedtoid, displayName: draft.dagrun_pm_assignedtoname ?? draft.dagrun_pm_assignedtoid } : null}
            onChange={p => {
              const fields = {
                dagrun_pm_assignedtoid: p?.aadObjectId ?? null,
                dagrun_pm_assignedtoname: p?.displayName ?? null,
              };
              setDraft(d => ({ ...d, ...fields })); patch(fields);
            }}
          />
        </div>

        {showAnswer && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={BM_LABEL}>Answer</label>
              <AutoSizingTextarea
                value={draft.dagrun_pm_answer ?? ''}
                onChange={v => setText('dagrun_pm_answer', v || null)}
                onBlur={v => flush('dagrun_pm_answer', v || null)}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={BM_LABEL}>Answered Date</label>
              <input type="date" value={draft.dagrun_pm_answereddate?.slice(0, 10) ?? ''}
                onChange={e => setAndPatch('dagrun_pm_answereddate', e.target.value ? new Date(e.target.value).toISOString() : null)} style={BM_INPUT} />
            </div>
          </>
        )}

        <ApprovalStagesSection
          pca={pca}
          stages={approvalStages}
          loading={stagesLoading}
          myOid={me?.oid}
          onDecision={onStageDecision}
          entityLabel="RFI"
          triggerStatusLabel="In Review"
        />

        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'flex-end' }}>
          <BMDangerButton onClick={() => setConfirmDelete(true)}>Delete</BMDangerButton>
        </div>
      </div>

      {confirmDelete && (
        <BoardModal open title="Delete RFI" onClose={() => setConfirmDelete(false)}
          footer={<>
            <BMCancelButton onClick={() => setConfirmDelete(false)} disabled={saving} />
            <BMDangerButton onClick={doDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</BMDangerButton>
          </>}>
          Delete RFI <strong style={{ color: '#0F1117' }}>{draft.dagrun_pm_rfinumber ?? draft.dagrun_pm_name}</strong>?
        </BoardModal>
      )}
    </>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '8px 12px', fontWeight: 500, fontSize: 12, color: '#5A6272', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>
);
const Em = () => <span style={{ color: '#B8BEC9', fontSize: 13 }}>—</span>;
